import { ShipmentType, SupplyShipmentStatusName, SupplyShipmentCarrier } from '@caresend/types';
import { isObject } from './basicGuards.js';
import { getNumberErrors, getNonEmptyStringErrors, getTimeZoneDateErrors, buildSpecificStringErrorGetter, buildStringEnumErrorsGetter, getOptionalErrors, getByIdErrors, getPositiveIntegerErrors } from './helpers.js';
import { getLocationObjectErrors } from './location.js';
import { getKeyValidationErrors } from './validation.js';

const shopifyInfoValidationKeys = {
  shopifyOrderID: getPositiveIntegerErrors,
  orderStatusURL: getNonEmptyStringErrors
};
const getShopifyInfoErrors = value => {
  if (isObject(value)) {
    return getKeyValidationErrors(value, shopifyInfoValidationKeys);
  }

  return `Expected object, found ${typeof value}`;
}; // TODO: For sample shipments, empty strings are saved for some values.
// Types should be adjusted to be optional and validation below should be
// adjusted. However, the non-empty string validation should be retained
// in a secondary validation function with stricter requirements for use
// where `getValidatedShipment` is currently used. Relevant keys are marked
// with TODO: optional.

const shipmentValidationKeys = {
  cost: getOptionalErrors(getNumberErrors),
  creationTimestamp: getNumberErrors,
  deliveryMethod: buildStringEnumErrorsGetter(SupplyShipmentCarrier),
  id: getNonEmptyStringErrors,
  placeID: getOptionalErrors(getNonEmptyStringErrors),
  shipBy: getOptionalErrors(getTimeZoneDateErrors),
  shipmentBoxSupplyID: getOptionalErrors(getNonEmptyStringErrors),
  shippingAddress: getLocationObjectErrors,
  shippingLabelBarcode: getNonEmptyStringErrors,
  shippingLabelURL: getOptionalErrors(getNonEmptyStringErrors),
  shippingRecipient: getNonEmptyStringErrors,
  shopifyInfo: getShopifyInfoErrors,
  status: true,
  trackingURL: getNonEmptyStringErrors,
  transferID: getOptionalErrors(getNonEmptyStringErrors),
  type: buildStringEnumErrorsGetter(ShipmentType)
};
const supplyShipmentValidationKeys = Object.assign(Object.assign({}, shipmentValidationKeys), {
  cost: getNumberErrors,
  placeID: getNonEmptyStringErrors,
  shipBy: getTimeZoneDateErrors,
  shippingLabelURL: getNonEmptyStringErrors,
  shipmentBoxSupplyID: getNonEmptyStringErrors,
  transferID: getNonEmptyStringErrors,
  type: buildSpecificStringErrorGetter(ShipmentType.SUPPLY),
  // had to make a custom wraper since SupplyShipmentStatus = Status<SupplyShipmentStatusName>
  status: status => buildStringEnumErrorsGetter(Object.fromEntries(Object.entries(SupplyShipmentStatusName).map(([k, v]) => [k, v]).filter(([k, _]) => k !== SupplyShipmentStatusName.DRAFT)))(status === null || status === void 0 ? void 0 : status.status)
});
const DraftSupplyShipmentValidationKeys = Object.assign(Object.assign({}, supplyShipmentValidationKeys), {
  // optional shopify info, filled-in by webhook
  cost: getOptionalErrors(getNumberErrors),
  shippingLabelURL: getOptionalErrors(getNonEmptyStringErrors),
  shippingLabelBarcode: getOptionalErrors(getNonEmptyStringErrors),
  trackingURL: getOptionalErrors(getNonEmptyStringErrors),
  shippingRecipient: getOptionalErrors(getNonEmptyStringErrors),
  // had to make a custom wraper since SupplyShipmentStatus = Status<SupplyShipmentStatusName>
  status: value => buildSpecificStringErrorGetter(SupplyShipmentStatusName.DRAFT)(value === null || value === void 0 ? void 0 : value.status)
});
const isShipmentValid = shipment => !getKeyValidationErrors(shipment, shipmentValidationKeys);
/**
 * Throws error if validation fails.
 */

const getValidatedShipment = shipment => {
  if (!isShipmentValid(shipment)) {
    throw Error(`Shipment does not pass validation:
      ${getKeyValidationErrors(shipment, shipmentValidationKeys)}`);
  }

  return shipment;
};
/**
 * Note: Every valid `SupplyShipment` is also a valid `Shipment`. This supply
 * shipment validator is more strict than the `SupplyShipment` type definition.
 */

const isSupplyShipmentValid = supplyShipment => !getKeyValidationErrors(supplyShipment, supplyShipmentValidationKeys);
/**
 * Throws error if validation fails.
 *
 * Note: Every valid `SupplyShipment` is also a valid `Shipment`. This supply
 * shipment validator is more strict than the `SupplyShipment` type definition.
 */

const getValidatedSupplyShipment = supplyShipment => {
  if (!isSupplyShipmentValid(supplyShipment)) {
    throw Error(`Supply shipment ${supplyShipment.id} does not pass validation:
      ${getKeyValidationErrors(supplyShipment, supplyShipmentValidationKeys)}`);
  }

  return supplyShipment;
};
const isDraftSupplyShipmentValid = shipment => !getKeyValidationErrors(shipment, DraftSupplyShipmentValidationKeys);
const getValidatedDraftSupplyShipment = supplyShipment => {
  if (!isDraftSupplyShipmentValid(supplyShipment)) {
    throw Error(`Draft supply shipment does not pass partial validation:
      ${getKeyValidationErrors(supplyShipment, DraftSupplyShipmentValidationKeys)}`);
  }

  return supplyShipment;
};
const getSupplyItemWithSupplyIDErrors = supplyItem => getKeyValidationErrors(supplyItem, {
  id: getNonEmptyStringErrors,
  supplyID: getNonEmptyStringErrors,
  relatedSupplyItems: getOptionalErrors(getByIdErrors(getSupplyItemWithSupplyIDErrors))
});

export { getShopifyInfoErrors, getSupplyItemWithSupplyIDErrors, getValidatedDraftSupplyShipment, getValidatedShipment, getValidatedSupplyShipment, isDraftSupplyShipmentValid, isShipmentValid, isSupplyShipmentValid };
